/**
 * SI-2-01 — minimal JSON-RPC 2.0 client over a message port (WebSocket).
 *
 * Pure request/response matching + notification dispatch. The actual socket
 * is injected so tests can drive the protocol without a real network, and so
 * the React hook can swap in `window.WebSocket` at mount time.
 */

export type JsonRpcId = string | number;

export type JsonRpcRequest = {
  jsonrpc: "2.0";
  id: JsonRpcId;
  method: string;
  params?: unknown;
};

export type JsonRpcSuccess = {
  jsonrpc: "2.0";
  id: JsonRpcId | null;
  result: unknown;
};

export type JsonRpcFailure = {
  jsonrpc: "2.0";
  id: JsonRpcId | null;
  error: { code: number; message: string; data?: unknown };
};

export type JsonRpcNotification = {
  jsonrpc: "2.0";
  method: string;
  params?: unknown;
};

export type JsonRpcInbound = JsonRpcSuccess | JsonRpcFailure | JsonRpcNotification;

export type MessagePortLike = {
  send: (data: string) => void;
  close: () => void;
  /** Assign to receive inbound text frames. */
  onMessage: ((data: string) => void) | null;
  onClose: ((ev?: { code?: number; reason?: string }) => void) | null;
  onError: ((err?: unknown) => void) | null;
  readonly readyState: number;
};

export const WS_CONNECTING = 0;
export const WS_OPEN = 1;
export const WS_CLOSING = 2;
export const WS_CLOSED = 3;

export type JsonRpcClient = {
  request: (method: string, params?: unknown) => Promise<unknown>;
  close: () => void;
};

export type JsonRpcClientOptions = {
  port: MessagePortLike;
  onNotification: (method: string, params: unknown) => void;
  /** Optional id factory; default is a monotonic counter. */
  nextId?: () => JsonRpcId;
};

/**
 * Bind a JSON-RPC client to a message port. Resolves/rejects pending requests
 * by id; routes messages without `id` to `onNotification`.
 */
export function createJsonRpcClient(opts: JsonRpcClientOptions): JsonRpcClient {
  let seq = 0;
  const nextId =
    opts.nextId ??
    (() => {
      seq += 1;
      return seq;
    });

  const pending = new Map<
    JsonRpcId,
    { resolve: (v: unknown) => void; reject: (e: Error) => void }
  >();

  const settleClosed = (reason: string) => {
    for (const [, p] of pending) {
      p.reject(new Error(reason));
    }
    pending.clear();
  };

  opts.port.onMessage = (raw: string) => {
    let msg: JsonRpcInbound;
    try {
      msg = JSON.parse(raw) as JsonRpcInbound;
    } catch {
      return;
    }
    if (!msg || typeof msg !== "object" || msg.jsonrpc !== "2.0") return;

    // Notification: method present, neither result nor error (host→client push).
    if (
      "method" in msg &&
      typeof (msg as JsonRpcNotification).method === "string" &&
      !("result" in msg) &&
      !("error" in msg)
    ) {
      opts.onNotification(
        (msg as JsonRpcNotification).method,
        (msg as JsonRpcNotification).params,
      );
      return;
    }

    const id = (msg as JsonRpcSuccess | JsonRpcFailure).id;
    if (id == null) return;
    const waiter = pending.get(id);
    if (!waiter) return;
    pending.delete(id);
    if ("error" in msg && msg.error) {
      waiter.reject(
        new Error(
          typeof msg.error.message === "string"
            ? msg.error.message
            : `jsonrpc error ${msg.error.code}`,
        ),
      );
      return;
    }
    waiter.resolve((msg as JsonRpcSuccess).result);
  };

  opts.port.onClose = () => {
    settleClosed("websocket closed");
  };
  opts.port.onError = () => {
    settleClosed("websocket error");
  };

  return {
    request(method: string, params?: unknown): Promise<unknown> {
      const id = nextId();
      const req: JsonRpcRequest = {
        jsonrpc: "2.0",
        id,
        method,
        ...(params !== undefined ? { params } : {}),
      };
      return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        try {
          opts.port.send(JSON.stringify(req));
        } catch (e) {
          pending.delete(id);
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      });
    },
    close() {
      settleClosed("client closed");
      try {
        opts.port.close();
      } catch {
        /* ignore */
      }
    },
  };
}

/**
 * Wrap a browser `WebSocket` as a MessagePortLike. Call only in environments
 * that provide WebSocket (browser / Node 22+ global).
 */
export function portFromWebSocket(ws: {
  send: (data: string) => void;
  close: () => void;
  readyState: number;
  addEventListener?: (
    type: string,
    listener: (ev: { data?: unknown; code?: number; reason?: string }) => void,
  ) => void;
  removeEventListener?: (
    type: string,
    listener: (ev: { data?: unknown; code?: number; reason?: string }) => void,
  ) => void;
  onmessage?: ((ev: { data: unknown }) => void) | null;
  onclose?: ((ev: { code?: number; reason?: string }) => void) | null;
  onerror?: ((ev: unknown) => void) | null;
}): MessagePortLike {
  const port: MessagePortLike = {
    send: (data) => ws.send(data),
    close: () => ws.close(),
    onMessage: null,
    onClose: null,
    onError: null,
    get readyState() {
      return ws.readyState;
    },
  };

  const onMessage = (ev: { data?: unknown }) => {
    const data = ev.data;
    if (typeof data === "string") port.onMessage?.(data);
  };
  const onClose = (ev: { code?: number; reason?: string }) => {
    port.onClose?.(ev);
  };
  const onError = (ev: unknown) => {
    port.onError?.(ev);
  };

  if (typeof ws.addEventListener === "function") {
    ws.addEventListener("message", onMessage);
    ws.addEventListener("close", onClose);
    ws.addEventListener("error", onError);
  } else {
    ws.onmessage = onMessage;
    ws.onclose = onClose;
    ws.onerror = onError;
  }

  return port;
}

/** Default same-origin WS URL for the live session host (S-D2). */
export function defaultSessionWsUrl(
  locationLike: { protocol: string; host: string } | null | undefined,
): string {
  if (!locationLike) return "ws://localhost:3000/api/sessions/ws";
  const proto = locationLike.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${locationLike.host}/api/sessions/ws`;
}
